import { membershipOfferBenefitTypes } from '@repo/types/membership-offer';
import { MembershipVariant } from '@repo/types/membership-variant';
import useSWR from 'swr';

import { findLocaleFromUrlAndCookie } from '@zooplus/commons-lang';

import { usePriceFormatter } from './usePriceFormatter';

import type { CustomerType } from '@repo/types/customer-overview';
import type {
  MembershipOffer,
  MembershipOfferBenefitType,
  MembershipOffersResponse,
  MembershipOverview,
  MembershipOfferInfo,
  MembershipExpirationStatus,
} from '@repo/types/membership-offer';

export function useMembershipOffer(shouldFetch: boolean = true) {
  return useSWR(
    shouldFetch ? '/semiprotected/api/loyalty-management/memberships/offers' : null,
    async (...args) => {
      const response = await fetch(...args, {
        headers: { locale: findLocaleFromUrlAndCookie() || 'en-GB' },
      });

      if (response.status !== 200) {
        return undefined;
      }

      const raw = (await response.json()) as MembershipOffersResponse;

      if (!Array.isArray(raw.offers) || raw.offers.length === 0) {
        return undefined;
      }

      return { offer: raw.offers[0], renewInfo: raw.renewInfo } as MembershipOfferInfo;
    },
    {
      errorRetryInterval: 1000,
      errorRetryCount: 3,
      dedupingInterval: 5000,
      revalidateOnFocus: false,
    },
  );
}

export function useMembershipExpirationStatus(renewInfo?: MembershipOverview) {
  const endDate = new Date(renewInfo?.endDate ? renewInfo.endDate : '');

  const getExpirationStatus = (): MembershipExpirationStatus => {
    if (!renewInfo) return 'NO_EXPIRE';
    if (renewInfo.status === 'EXPIRED') return 'AFTER_EXPIRE';
    if (renewInfo.status === 'ACTIVE' && endDate) {
      const msUntilEnd = new Date(endDate).getTime() - Date.now();
      if (msUntilEnd < 42 * 24 * 60 * 60 * 1000) return 'BEFORE_EXPIRE';
    }
    return 'NO_EXPIRE';
  };

  return { getExpirationStatus, endDate };
}

export function getOfferDiscount(offer: MembershipOffer) {
  const percentageDiscountBenefit = offer.benefits.find(
    (benefit): benefit is Extract<MembershipOfferBenefitType, { type: 'PERCENTAGE_DISCOUNT' }> =>
      benefit.type === 'PERCENTAGE_DISCOUNT',
  );

  return percentageDiscountBenefit ? percentageDiscountBenefit.discount : undefined;
}

/** Given an offer and articles subtotal, return the potential savings, if available,
 * formatted with the offer currency and rounded */
export function useMembershipOfferSavings(offer: MembershipOffer, articlesSubtotal?: number) {
  const percentageDiscountBenefit = getOfferDiscount(offer);
  const currency = offer.price.currency;
  const formatPrice = usePriceFormatter();

  return percentageDiscountBenefit && articlesSubtotal
    ? formatPrice((percentageDiscountBenefit / 100) * articlesSubtotal, currency, true)
    : undefined;
}

/** Given a customer type and a membership overview (if available),
 * return the variant of the membership: basic | premium | beforeExpire | expired */
export function useMembershipVariant(
  customerType: CustomerType,
  renewInfo?: MembershipOverview,
): MembershipVariant {
  const { getExpirationStatus } = useMembershipExpirationStatus(renewInfo);
  const expirationStatus = getExpirationStatus();

  if (customerType === 'basic' && expirationStatus === 'NO_EXPIRE') {
    return 'basic';
  }
  if (customerType === 'premium' && expirationStatus === 'NO_EXPIRE') {
    return 'premium';
  }
  if (expirationStatus === 'BEFORE_EXPIRE') {
    return 'beforeExpire';
  }
  if (expirationStatus === 'AFTER_EXPIRE') {
    return 'expired';
  }
  return 'basic';
}

/** Return the offer benefits, adding hardcoded benefits and filtering out unknown types.
 * The filter array can be used to only return a subset of benefits.
 */
export function getOfferBenefits(
  benefits: Array<MembershipOfferBenefitType>,
  filterArray: Array<MembershipOfferBenefitType['type']> = membershipOfferBenefitTypes,
) {
  return benefits
    .concat([{ type: 'EXCLUSIVE_OFFERS' }, { type: 'NO_AUTOMATIC_RENEWAL' }])
    .filter((benefit) => filterArray.includes(benefit.type));
}
